import { useNavigation } from "@react-navigation/native";
import { Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { ONBOARDING_NAVIGATION_ERROR } from "../../constants/onboardingMessages";
import ScreenNames from "../../constants/routeNames";
import { getNextScreen } from "../../navigation/simpleOnboardingFlow";
import type { MealTypeEnum } from "../../services/backendTypes";
import { selectMealTimes, setMealTimes } from "../../slices/simpleOnboardingSlice";
import SelectionScreen, { type Option } from "../New_DIY_form/SelectionScreen";

const validationSchema = Yup.object().shape({
  mealTimes: Yup.array()
    .of(Yup.string().oneOf(["BREAKFAST", "LUNCH", "SNACK", "DINNER"]))
    .min(2, "Please select at least two meal times")
    .test("dinner-required", "Dinner must be selected", (value) => (value ? value.includes("DINNER") : false))
    .required("Please select when you typically eat"),
});

const SimpleOnboardingMealTimesScreen = (): JSX.Element => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const dispatch = useDispatch();

  const selectedMealTimes = useSelector(selectMealTimes);

  const options: Option[] = [
    {
      value: "BREAKFAST" as MealTypeEnum,
      label: t("simple_onboarding.meal_times.BREAKFAST"),
      description: "",
      icon: "Croissant",
      iconType: "lucide",
    },
    {
      value: "LUNCH" as MealTypeEnum,
      label: t("simple_onboarding.meal_times.LUNCH"),
      description: "",
      icon: "Sandwich",
      iconType: "lucide",
    },
    {
      value: "SNACK" as MealTypeEnum,
      label: t("simple_onboarding.meal_times.SNACK"),
      description: "",
      icon: "Apple",
      iconType: "lucide",
    },
    {
      value: "DINNER" as MealTypeEnum,
      label: t("simple_onboarding.meal_times.DINNER"),
      description: "",
      icon: "Utensils",
      iconType: "lucide",
    },
  ];

  // Default to breakfast, lunch, and dinner selected
  const defaultMealTimes = ["BREAKFAST", "LUNCH", "DINNER"] as MealTypeEnum[];

  return (
    <Formik
      initialValues={{ mealTimes: selectedMealTimes.length > 0 ? selectedMealTimes : defaultMealTimes }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        dispatch(setMealTimes(values.mealTimes));
        const nextScreen = getNextScreen(ScreenNames.SimpleOnboarding_5_MealTimes);
        if (nextScreen) {
          navigation.navigate(nextScreen as never);
        } else {
          alert(ONBOARDING_NAVIGATION_ERROR);
        }
      }}
      validate={(values) => {
        const errors: { mealTimes?: string } = {};
        if (values.mealTimes.length < 2) {
          errors.mealTimes = t("simple_onboarding.meal_times.min_two_required");
        } else if (!values.mealTimes.includes("DINNER")) {
          errors.mealTimes = t("simple_onboarding.meal_times.dinner_required");
        }
        return errors;
      }}
    >
      {({ values, setFieldValue, errors, handleSubmit, isValid }) => (
        <SelectionScreen
          title={t("simple_onboarding.meal_times.title")}
          subtitle=""
          options={options}
          multipleSelection={true}
          selectedOptions={values.mealTimes}
          setSelectedOptions={(mealTimeOptions) => setFieldValue("mealTimes", mealTimeOptions)}
          onPressNext={handleSubmit}
          onPressBack={() => navigation.goBack()}
          nextButtonDisabled={!isValid || values.mealTimes.length < 2 || !values.mealTimes.includes("DINNER")}
          errorMessage={errors.mealTimes}
          nextButtonTestIdPostfix="meal-times"
        />
      )}
    </Formik>
  );
};

export default SimpleOnboardingMealTimesScreen;
